import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';

import { Site } from '../../objects/site';
import { SiteService } from '../../service/entity-services/site.service';

// Angular Material Imports
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatChipsModule } from '@angular/material/chips';

import { AgGridModule } from 'ag-grid-angular';
import * as contractColumns from '../../shared/entity-columns/contract-columns';

@Component({
  selector: 'app-site',
  templateUrl: './site.component.html',
  styleUrls: ['./site.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    AgGridModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatDividerModule,
    MatFormFieldModule,
    MatInputModule,
    MatSlideToggleModule,
    MatProgressSpinnerModule,
    MatChipsModule
  ]
})
export class SiteComponent implements OnInit, OnDestroy {

  // Inject services using modern pattern
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private siteService = inject(SiteService);

  // Component state
  currentSite: Site = {};
  userRole: string | null = null;
  reviewer = true;
  loading = true;

  // Grid state
  gridApi: any = null;
  contractsAssociatedWithThisSite: any[] = [];

  defaultColDef = {
    resizable: true,
    sortable: true,
    filter: true
  };

  contractCols = [
    contractColumns.colContractId,
    contractColumns.colContractName,
    contractColumns.colType,
    contractColumns.colContractStatus,
    contractColumns.colContractLink
  ];

  // Subscriptions
  private subscriptions = new Subscription();

  constructor() {}

  ngOnInit() {
    // Get site number from route params
    const routeSub = this.route.params.subscribe(params => {
      // The route parameter is named 'id' in the route config
      const siteNum = params['id'];

      if (siteNum) {
        console.log('Loading site with number:', siteNum);
        this.loadSite(siteNum);
      } else {
        console.error('No site number provided in route');
        this.loading = false;
      }
    });

    this.subscriptions.add(routeSub);
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  /**
   * Load site data from service
   */
  private loadSite(siteNum: string) {
    this.loading = true;

    const siteSub = this.siteService.allSites$.subscribe({
      next: (sites) => {
        const foundSite = sites.find(
          site => site.siteNum === siteNum
        );

        if (foundSite) {
          this.currentSite = foundSite;
          console.log('Loaded site:', this.currentSite);

          // Load associated contracts if needed
          this.loadAssociatedContracts(siteNum);
        } else {
          console.warn('Site not found:', siteNum);
        }

        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading site:', error);
        this.loading = false;
      }
    });

    this.subscriptions.add(siteSub);
  }

  /**
   * Load contracts associated with this site
   */
  private loadAssociatedContracts(siteNum: string) {
    // TODO: Implement when contract service method is available
    console.log('Associated contracts loading not yet implemented');
  }

  /**
   * Navigate to edit mode
   */
  editSite() {
    if (this.currentSite.siteNum) {
      this.router.navigate(['/securehome/new-site'], {
        state: { site: this.currentSite, editMode: true }
      });
    }
  }

  /**
   * Navigate back to sites list
   */
  goBack() {
    this.router.navigateByUrl('/securehome/sites');
  }

  /**
   * Handle grid cell clicks
   */
  onCellClicked(event: any) {
    console.log('Clicked on contract:', event.data);

    if (event.data.contractNum) {
      this.router.navigate(['/securehome/contract', event.data.contractNum]);
    }
  }

  /**
   * Handle grid ready event
   */
  onGridReady(params: any) {
    this.gridApi = params.api;
    this.gridApi.sizeColumnsToFit();
  }

  /**
   * Helper getters for template
   */
  get isInactive(): boolean {
    return this.currentSite.inactive ?? false;
  }

  get canEdit(): boolean {
    return this.userRole !== 'Viewer';
  }

  get hasContracts(): boolean {
    return this.contractsAssociatedWithThisSite.length > 0;
  }
}
